<template>
  <DeepDialog
    v-model="showDialog"
    fullscreen-on-mobile
    :title="i18n.t('titles.reject_signature')"
    width="600"
    @keydown.enter="reject"
    @keydown.esc="() => (showDialog = false)"
  >
    <template #content>
      <div class="text-black text-body-large mb-4">
        {{ i18n.t('texts.reject_signature') }}
      </div>

      <div class="text-body-medium text-medium-emphasis mb-1">
        {{ i18n.t('titles.reject_signature_text') }}
      </div>

      <v-textarea
        v-model="comment"
        no-resize
        :placeholder="i18n.t('placeholders.reject_signature')"
        variant="outlined"
      />
    </template>
    <template #actions>
      <VBtnSecondary
        :text="i18n.t('buttons.cancel')"
        variant="outlined"
        @click="() => (showDialog = false)"
      />
      <VBtnPrimary
        :loading="signStore.rejectSignaturePending"
        :text="i18n.t('buttons.reject')"
        @click="reject"
      />
    </template>
  </DeepDialog>
</template>

<script setup lang="ts">
import AuthService from '@/services/authService.ts';
import { useSignStore } from '@/stores/sign.ts';
import { computed, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useEmitter } from '@/composables/useEmitter';
import { useMessageStore } from '@/stores/message.ts';
import { DeepDialog } from '@deepcloud/deep-ui-lib';

const i18n = useI18n();
const emitter = useEmitter();
const signStore = useSignStore();
const router = useRouter();
const route = useRoute();
const showDialog = ref(false);
const comment = ref(null);
const messageStore = useMessageStore();

const document_id = computed(() => {
  if (signStore.document && signStore.document.documentId) {
    return signStore.document.documentId;
  } else {
    return Array.isArray(route.params.document)
      ? route.params.document[0]
      : route.params.document;
  }
});

emitter.$on('open-reject-dialog', () => (showDialog.value = true));

onUnmounted(() => {
  emitter.$off('open-reject-dialog', () => (showDialog.value = true));
});

const signKey = computed(() => {
  if (signStore.document && signStore.document.signContext.signKey) {
    return signStore.document.signContext.signKey;
  } else return undefined;
});

async function reject() {
  try {
    if (signKey.value) {
      const payload = {
        comment: comment.value
      };
      const message = {
        key: 'snackbars.signature_rejected',
        color: 'success',
        icon: 'fa fa-circle-check'
      };
      await signStore.rejectSignature(payload, document_id.value);
      showDialog.value = false;
      messageStore.showMessage(message);
      if (AuthService.isAuthenticated) {
        await router.push({
          name: 'document-details',
          params: { document: document_id.value }
        });
        emitter.$emit('update-sign-status');
      } else {
        window.location.reload();
      }
    }
  } catch (error) {
    showDialog.value = false;
    messageStore.showMessage({
      key: 'errors.api.reject_signature',
      color: 'error',
      icon: 'fa fa-circle-exclamation'
    });
    console.error(
      'Error rejecting signature, signKey : ',
      signKey.value,
      error
    );
  }
}
</script>

<style lang="scss" scoped>
.cancel-button {
  margin-bottom: 10px;
}
</style>
